/**
 * Updates on web and desktop ("About & updates" canvas; core logic/updates.ts). Chrome and Slack's model: find a new
 * build, get it ready quietly, apply it on its own only while nobody is using the app, never interrupt.
 *
 * Web: a deploy changes /version.json (and the PWA service worker, vite-plugin-pwa `prompt` mode). A tab looks every
 * 15 minutes and when it is shown, focused or back online (at most once a minute; webUpdate.ts). Page loads are
 * network-first in the worker, so a reload or a new tab always gets the live build. When a newer build is live and the tab is safe to reload (hidden or idle,
 * no composer text, call, send or upload: Updates.safeToRestart) it reloads by itself on the same route; otherwise it
 * offers "Reload to update" (toast, Help menu, About).
 *
 * Desktop: Rust owns the updater (apps/desktop/src-tauri/src/updater.rs): it checks at launch and every 6 h,
 * downloads and verifies, and sends `bundle:update-status`. Each window reports every 30 s whether it is safe to
 * restart; when all are (or there are no windows), Rust installs, saves the open windows and relaunches. Quitting with
 * an update waiting installs it too.
 */
import { Updates, type BuildInfo, type UpdateStatus, type VersionInfo } from "@bundle/core";
import { currentActivity, startActivityTracking } from "./activity.ts";
import { checkDesktopUpdates, desktopInfo, installDesktopUpdate, isDesktop, onDesktopUpdateReady, onDesktopUpdateStatus, reportDesktopActivity, type DesktopMenuAction } from "./desktop.ts";
import { API_BASE, BUILD_COMMIT, BUILD_TIME } from "./env.ts";
import { CHECK_EVERY_MS, applyWebUpdate, shouldCheck } from "./webUpdate.ts";

export type PendingUpdate = { kind: "web" } | { kind: "desktop"; version: string };

const SAFE_CHECK_EVERY_MS = 30 * 1000;
/** Set before an automatic reload/restart, read once after it: shows "Updated to …". */
const UPDATED_FLAG = "bundle.updated";
const LAST_RUN_KEY = "bundle.lastRunBuild";

let pending: PendingUpdate | undefined;
let dismissed = false;
let status: UpdateStatus = { kind: "idle" };
let swRegistration: ServiceWorkerRegistration | undefined;
let aboutOpen = false;
let updatedTo: string | undefined;
let server: BuildInfo | undefined;
let started = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of Array.from(listeners)) l();
}

function setStatus(next: UpdateStatus): void {
  status = next;
  if (next.kind === "ready") {
    const p: PendingUpdate = isDesktop() ? { kind: "desktop", version: next.version } : { kind: "web" };
    if (pending === undefined || pending.kind !== p.kind || (p.kind === "desktop" && pending.kind === "desktop" && pending.version !== p.version)) dismissed = false;
    pending = p;
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The "new version" toast (shell/UpdateToast.tsx). */
export const updates = {
  subscribe,
  getSnapshot: (): PendingUpdate | undefined => (dismissed ? undefined : pending),
  /** Reload into the new web build, or install the desktop update and restart. */
  async apply(): Promise<void> {
    const p = pending;
    if (p === undefined) return;
    markUpdating();
    if (p.kind === "desktop") await installDesktopUpdate();
    else reloadWeb();
  },
  /** Hide the toast until the next update is found (the Help menu row and About keep offering it). */
  dismiss(): void {
    dismissed = true;
    emit();
  },
};

/** Help menu, About and the macOS menu: the status, Check for updates, and the About dialog. */
export const updateStatus = {
  subscribe,
  getSnapshot: (): UpdateStatus => status,
  checkNow,
  restartNow: (): Promise<void> => updates.apply(),
};

export const about = {
  subscribe,
  getSnapshot: (): boolean => aboutOpen,
  open(): void {
    aboutOpen = true;
    emit();
    void loadServer();
  },
  close(): void {
    aboutOpen = false;
    emit();
  },
};

/** "Updated to Bundle 0.2.21" after an automatic restart or reload (once). */
export const updatedToast = {
  subscribe,
  getSnapshot: (): string | undefined => updatedTo,
  dismiss(): void {
    updatedTo = undefined;
    emit();
  },
};

/** What About shows. `server` fills in after About first opens. */
export function versionInfo(): VersionInfo {
  const d = desktopInfo();
  const web: BuildInfo = { commit: BUILD_COMMIT, builtAt: BUILD_TIME };
  const channel = import.meta.env.PROD && d?.channel !== "dev" ? "stable" : "dev";
  const info: VersionInfo = { platform: d === null ? "web" : "desktop", channel, web };
  if (d !== null) info.app = { version: d.version, commit: d.commit ?? null, os: [osName(d.platform), d.arch].filter((s) => s !== undefined && s !== "").join(" ") };
  if (server !== undefined) info.server = server;
  return info;
}

function osName(p: "macos" | "windows" | "linux"): string {
  return p === "macos" ? "macOS" : p === "windows" ? "Windows" : "Linux";
}

let serverLoad: Promise<void> | undefined;
function loadServer(): Promise<void> {
  serverLoad ??= fetch(`${API_BASE}/v1/version`, { cache: "no-store" })
    .then((r) => (r.ok ? (r.json() as Promise<{ commit: string | null; builtAt: string | null }>) : undefined))
    .then((v) => {
      if (v !== undefined) {
        server = { commit: v.commit, builtAt: v.builtAt };
        emit();
      }
    })
    .catch(() => {
      serverLoad = undefined; // try again next time About opens
    });
  return serverLoad;
}

// ---------- Web ----------

async function liveWebBuild(): Promise<Updates.WebBuild> {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`version.json ${res.status}`);
  return (await res.json()) as Updates.WebBuild;
}

async function checkWeb(manual: boolean): Promise<void> {
  if (manual) setStatus({ kind: "checking" });
  try {
    const live = await liveWebBuild();
    if (Updates.webBuildChanged({ commit: BUILD_COMMIT }, live)) {
      // Let the service worker fetch the new shell too, so the reload is instant and offline-safe.
      swRegistration?.update().catch(() => undefined);
      setStatus({ kind: "ready", version: Updates.shortCommit(live.commit) });
    } else if (status.kind !== "ready") {
      setStatus({ kind: "upToDate", version: Updates.shortCommit(BUILD_COMMIT), checkedAt: Date.now() });
    }
  } catch (e) {
    if (manual) setStatus({ kind: "error", during: "check", message: e instanceof Error ? e.message : String(e) });
  }
}

let reloading = false;
function reloadWeb(): void {
  if (reloading) return;
  reloading = true;
  applyWebUpdate({
    registration: swRegistration,
    container: "serviceWorker" in navigator ? navigator.serviceWorker : undefined,
    reload: () => location.reload(),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  });
}

function markUpdating(): void {
  try {
    sessionStorage.setItem(UPDATED_FLAG, "1");
  } catch {
    // private mode: no toast afterwards
  }
}

// ---------- Both ----------

async function checkNow(): Promise<void> {
  if (isDesktop()) {
    setStatus({ kind: "checking" });
    try {
      await checkDesktopUpdates();
    } catch (e) {
      setStatus({ kind: "error", during: "check", message: e instanceof Error ? e.message : String(e) });
    }
    return;
  }
  if (!import.meta.env.PROD || BUILD_COMMIT === null) {
    setStatus({ kind: "upToDate", version: Updates.shortCommit(BUILD_COMMIT), checkedAt: Date.now() });
    return;
  }
  await checkWeb(true);
}

/** Every 30 s: is this window safe to restart? Web reloads itself; desktop tells Rust, which decides for all windows. */
function watchSafety(): void {
  const tick = () => {
    const safe = Updates.safeToRestart(currentActivity(), Date.now());
    if (isDesktop()) {
      void reportDesktopActivity(safe).catch(() => undefined);
    } else if (safe && status.kind === "ready") {
      markUpdating();
      reloadWeb();
    }
  };
  setInterval(tick, SAFE_CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", tick);
  tick();
}

/** After a restart or reload into a new build: "Updated to Bundle x" (only when an update caused it). */
function noteUpdated(): void {
  const d = desktopInfo();
  const current = d?.version ?? Updates.shortCommit(BUILD_COMMIT);
  try {
    const flagged = sessionStorage.getItem(UPDATED_FLAG) === "1";
    sessionStorage.removeItem(UPDATED_FLAG);
    const last = localStorage.getItem(LAST_RUN_KEY) ?? undefined;
    localStorage.setItem(LAST_RUN_KEY, current);
    // Desktop restarts start a fresh webview (no sessionStorage): the version moving is the signal there.
    if (current !== "" && Updates.updatedSince(last, current) && (flagged || d !== null)) updatedTo = current;
  } catch {
    // storage blocked
  }
}

async function listenToDesktopMenu(): Promise<void> {
  const { getCurrentWebviewWindow } = await import("@tauri-apps/api/webviewWindow");
  await getCurrentWebviewWindow().listen<DesktopMenuAction>("bundle:menu", (e) => {
    if (e.payload.action === "about") about.open();
    else if (e.payload.action === "check-updates") {
      about.open();
      void checkNow();
    }
  });
}

/** Registers the service worker (production web builds) or the desktop listeners, and the safety watch. Call once. */
export function startUpdateChecks(): void {
  if (started) return;
  started = true;
  startActivityTracking();
  noteUpdated();
  if (isDesktop()) {
    void onDesktopUpdateStatus((s) => setStatus(s));
    // Older shells without update-status still send update-ready.
    void onDesktopUpdateReady((u) => {
      if (status.kind !== "ready") setStatus({ kind: "ready", version: u.version, notes: u.notes ?? null });
    });
    void listenToDesktopMenu().catch(() => undefined);
    watchSafety();
    return; // The desktop app ships its own web build; no service worker.
  }
  if (!import.meta.env.PROD) return;
  watchSafety();
  let lastCheckAt: number | undefined;
  const check = (force = false) => {
    if (!navigator.onLine || !shouldCheck(lastCheckAt, Date.now(), force)) return;
    lastCheckAt = Date.now();
    void checkWeb(false);
    if (swRegistration !== undefined && swRegistration.installing === null) swRegistration.update().catch(() => undefined);
  };
  setInterval(() => check(true), CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
  window.addEventListener("focus", () => check());
  window.addEventListener("online", () => check());
  // A lazy chunk that 404s usually means a deploy replaced it: look now, so the toast offers the reload.
  window.addEventListener("vite:preloadError", () => check(true));
  // A load that fell back to the offline shell (no network, or slower than the worker's timeout) may be behind.
  check(true);
  if (!("serviceWorker" in navigator)) return;
  void import("virtual:pwa-register").then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onNeedRefresh: () => {
        if (status.kind !== "ready") setStatus({ kind: "ready", version: "" });
      },
      onRegisteredSW: (_url, registration) => {
        swRegistration = registration;
      },
    });
  });
}
